/* =============================================================================
 * Copyright (c) 2026 SKYXOPS Corp. All rights reserved.
 * ============================================================================= */
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { Alert, AlertDescription, AlertTitle, Button, Input } from '@skyxops/ui';
import { acceptInvite, ApiError, checkInvite, InviteInfo, scrubUrlParams } from '../api';
import { authOrigin } from '../authOrigin';

const SKYXOPS_LOGO = 'https://appstatic.skyxops.io/skyxops/SKYXOPS-Logo.png';
const GOOGLE_ICON = 'https://appstatic.skyxops.io/login-icons/Google.png';
const MICROSOFT_ICON = 'https://appstatic.skyxops.io/login-icons/Microsoft.png';

export function AcceptInvitePage() {
  const navigate = useNavigate();
  const intl = useIntl();
  const t = (id: string, values?: Record<string, string>) =>
    intl.formatMessage({ id }, values);
  const [params] = useSearchParams();
  // AI-LAUNCH-45: capture the invite token ONCE, then scrub it from the address
  // bar below. It is a one-time credential and must not sit in browser history
  // or ride an outbound Referer. The lazy initialiser keeps the value after the
  // URL no longer carries it.
  const [token] = useState(() => params.get('token') ?? '');

  const [info, setInfo] = useState<InviteInfo | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setLoadError(t('invite.error_missing_token'));
      return;
    }
    scrubUrlParams('token');
    checkInvite(token)
      .then(setInfo)
      .catch((e) =>
        setLoadError(e instanceof ApiError ? e.message : t('invite.error_validate')),
      );
  }, [token]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < 12) {
      setError(t('invite.error_password_short'));
      return;
    }
    if (password !== confirm) {
      setError(t('invite.error_password_mismatch'));
      return;
    }
    setBusy(true);
    try {
      await acceptInvite(token, password, displayName.trim() || undefined);
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  function onSso(provider: 'Google' | 'Microsoft') {
    // Carry the invite token (+ login_hint) into the OAuth flow. The account is
    // created by the SSO callback ONLY if the verified federated email matches
    // the invite — nothing is provisioned here.
    // The invite token rides this redirect, so an unowned origin would exfiltrate
    // a one-time credential. Refuse rather than guess (AI-LAUNCH-40).
    const origin = authOrigin();
    if (!origin) {
      setError(t('login.error_sso_unconfigured_site'));
      return;
    }
    setBusy(true);
    const hint = info?.email ? `&login_hint=${encodeURIComponent(info.email)}` : '';
    window.location.href =
      `${origin}/api/v1/auth/sso/start?provider=${provider.toLowerCase()}` +
      `&invite=${encodeURIComponent(token)}${hint}`;
  }

  // Same shell + card as LoginPage: gradient bg, logo INSIDE a rounded-2xl card.
  const shell = (children: React.ReactNode) => (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 via-white to-violet-50 p-4 dark:from-skyxops-deepMidnight dark:via-skyxops-midnight dark:to-skyxops-ink">
      <div className="w-full max-w-md">
        <div className="rounded-2xl border border-border/60 bg-card p-8 shadow-xl">
          <div className="mb-7 flex justify-center">
            <img src={SKYXOPS_LOGO} alt="SKYXOPS" className="h-9 w-auto" />
          </div>
          {children}
        </div>
        <p className="mt-6 text-center text-xs text-muted-foreground">
          <FormattedMessage id="login.footer" values={{ year: '2026' }} />
        </p>
      </div>
    </div>
  );

  const heading = (title: string, subtitle?: React.ReactNode) => (
    <div className="mb-6 space-y-1 text-center">
      <h1 className="text-xl font-semibold tracking-tight text-foreground">{title}</h1>
      {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
    </div>
  );

  const fieldLabel = 'text-sm font-medium text-foreground';

  if (loadError) {
    return shell(
      <>
        {heading(t('invite.problem_title'))}
        <Alert variant="destructive" className="mb-4">
          <AlertTitle><FormattedMessage id="invite.problem_alert" /></AlertTitle>
          <AlertDescription>{loadError}</AlertDescription>
        </Alert>
        <Button className="w-full" variant="outline" onClick={() => navigate('/login')}>
          <FormattedMessage id="invite.go_to_signin" />
        </Button>
      </>,
    );
  }

  if (done) {
    return shell(
      <>
        {heading(t('invite.done_title'), t('invite.done_subtitle'))}
        <Button
          className="w-full bg-skyxops-midnight text-white hover:bg-skyxops-midnight/90"
          onClick={() => navigate('/login', { replace: true })}
        >
          <FormattedMessage id="invite.continue_signin" />
        </Button>
      </>,
    );
  }

  return shell(
    <>
      {heading(
        t('invite.title'),
        info ? (
          <FormattedMessage
            id={(info.org_name ?? info.msp_name) ? 'invite.subtitle_org' : 'invite.subtitle'}
            values={{
              email: <strong>{info.email}</strong>,
              org: info.org_name ?? info.msp_name ?? '',
            }}
          />
        ) : (
          t('invite.validating')
        ),
      )}

      {/* SSO accept works for both staff and client invites (AI-AUTH-9 —
          the callback finalizes either kind on verified email match). */}
      <div className="space-y-2">
        <button type="button" onClick={() => onSso('Google')} disabled={busy || !info}
          className="flex w-full items-center justify-center gap-2 rounded-md border border-border bg-background px-4 py-2 text-sm font-medium text-foreground transition hover:bg-muted disabled:opacity-50">
          <img src={GOOGLE_ICON} alt="" className="h-4 w-4" /> <FormattedMessage id="login.sso_google" />
        </button>
        <button type="button" onClick={() => onSso('Microsoft')} disabled={busy || !info}
          className="flex w-full items-center justify-center gap-2 rounded-md border border-border bg-background px-4 py-2 text-sm font-medium text-foreground transition hover:bg-muted disabled:opacity-50">
          <img src={MICROSOFT_ICON} alt="" className="h-4 w-4" /> <FormattedMessage id="login.sso_microsoft" />
        </button>
      </div>
      <div className="my-5 flex items-center gap-3">
        <span className="h-px flex-1 bg-border" />
        <span className="text-xs uppercase tracking-wide text-muted-foreground"><FormattedMessage id="invite.divider" /></span>
        <span className="h-px flex-1 bg-border" />
      </div>

      <form onSubmit={onSubmit} className="space-y-4">
        <div className="space-y-1.5">
          <label htmlFor="dn" className={fieldLabel}><FormattedMessage id="invite.label_name" /></label>
          <Input id="dn" value={displayName} onChange={(e) => setDisplayName(e.target.value)}
                 placeholder={t('invite.placeholder_name')} disabled={busy || !info} />
        </div>
        <div className="space-y-1.5">
          <label htmlFor="pw" className={fieldLabel}><FormattedMessage id="invite.label_password" /></label>
          <Input id="pw" type="password" autoComplete="new-password" placeholder={t('invite.placeholder_password')}
                 value={password} onChange={(e) => setPassword(e.target.value)} disabled={busy || !info} required />
        </div>
        <div className="space-y-1.5">
          <label htmlFor="cpw" className={fieldLabel}><FormattedMessage id="invite.label_confirm" /></label>
          <Input id="cpw" type="password" autoComplete="new-password" placeholder={t('invite.placeholder_confirm')}
                 value={confirm} onChange={(e) => setConfirm(e.target.value)} disabled={busy || !info} required />
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertTitle><FormattedMessage id="invite.error_title" /></AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <Button type="submit" className="w-full bg-skyxops-midnight text-white hover:bg-skyxops-midnight/90"
                disabled={busy || !info || !password || !confirm}>
          {busy ? t('invite.submitting') : t('invite.submit')}
        </Button>
      </form>
    </>,
  );
}
